import { useEffect, useMemo, useState } from "react";
import { Button, Flex, Text, Textarea } from "@chakra-ui/react";
import { useApolloClient, useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { gql } from "__generated__/gql";
import MarkdownText from "components/Shared/MarkdownText";
import MarkdownHelpText from "components/Shared/MarkdownHelpText";
import PageLoadingSpinner from "components/Shared/PageLoadingSpinner";
import Form from "components/Shared/Forms/Form";
import createFormFieldComponents from "components/Shared/Forms/FormField";
import useCreateValidSubmitHandler from "components/Shared/Forms/useCreateValidSubmitHandler";
import { assertPresence } from "utils/assertions";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { useQuery } from "utils/apollo/hooks";
import entityContextMarkdownSchema from "utils/schemas/entityContext";
import getUpdateEntityContextVariables from "utils/updateEntityContext";
import Well from "components/Shared/Well";
import CopyButton from "components/Shared/CopyButton";
import EditButton from "components/Shared/EditButton";

const FORM_ID = "retailer-context-form";

const query = gql(/* GraphQL */ `
  query AdminRetailerContext($id: UUID!) {
    retailer(id: $id) {
      id
      entityContext {
        id
        markdown
      }
    }
  }
`);

const mutation = gql(/* GraphQL */ `
  mutation AdminUpdateRetailerContext(
    $id: UUID!
    $data: RetailerPatch!
    $isEntityContextDeleted: Boolean = false
    $shouldSaveEntityContext: Boolean = false
    $entityContextId: UUID!
    $entityContextInput: UpdateOrCreateEntityContextInput!
  ) {
    ...UpdateEntityContext
    updateRetailer(input: { id: $id, patch: $data }) {
      retailer {
        id
        entityContext {
          id
          markdown
        }
      }
    }
    ...DeleteEntityContext
  }
`);

type Inputs = {
  markdown: string;
};

const schema: yup.ObjectSchema<Inputs> = yup.object().shape({
  markdown: entityContextMarkdownSchema.defined().default(""),
});

const { FormField } = createFormFieldComponents<Inputs>({ schema });

type Props = {
  retailerId: UUID | null;
};

export default function RetailerContext({ retailerId }: Props) {
  const apolloClient = useApolloClient();
  const [isEditing, setIsEditing] = useState(false);
  const { data, loading: isLoading } = useQuery(query, {
    context: ADMIN_CONTEXT,
    skip: !retailerId,
    variables: retailerId ? { id: retailerId } : undefined,
    fetchPolicy: "cache-and-network",
  });
  const retailer = data?.retailer;

  const defaultValues = useMemo(
    () => ({ markdown: retailer?.entityContext?.markdown || "" }),
    [retailer?.entityContext?.markdown],
  );

  const formContext = useForm<Inputs, object>({
    defaultValues,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    formContext.reset(defaultValues);
  }, [defaultValues, formContext]);

  const [performUpdate] = useMutation(mutation, { context: ADMIN_CONTEXT });

  const [handleValidSubmit, { isLoading: isSaving }] =
    useCreateValidSubmitHandler(
      async (formData: Inputs) => {
        assertPresence(retailer, "Expected retailer to be available.");

        const { relatedEntityContextId, ...entityContextVariables } =
          await getUpdateEntityContextVariables(
            apolloClient,
            retailer.entityContext?.id,
            formData.markdown,
          );

        await performUpdate({
          variables: {
            id: retailer.id,
            data: { entityContextId: relatedEntityContextId },
            ...entityContextVariables,
          },
        });
      },
      {
        successMessage: "Retailer context updated successfully.",
        onUpdateComplete: () => {
          setIsEditing(false);
        },
      },
    );

  if (isLoading && !retailer) {
    return <PageLoadingSpinner flex="1 1 100%" height="100%" />;
  }

  return (
    <Flex direction="column" flex="1 1 100%" minH={0}>
      <Flex justifyContent="space-between" alignItems="center" pb={3} gap={3}>
        <Text fontSize="sm" color="gray.600">
          Provide model context for this retailer (internally accessible only):
        </Text>

        {isEditing ? (
          <Flex gap={2}>
            <Button
              size="xs"
              variant="outline"
              colorScheme="pinkButton"
              onClick={() => {
                formContext.reset(defaultValues);
                setIsEditing(false);
              }}
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              colorScheme="pinkButton"
              type="submit"
              form={FORM_ID}
              isLoading={isSaving}
            >
              Save
            </Button>
          </Flex>
        ) : (
          <EditButton size="xs" onClick={() => setIsEditing(true)}>
            Edit Context
          </EditButton>
        )}
      </Flex>

      {isEditing ? (
        <Form
          formContext={formContext}
          id={FORM_ID}
          onValidSubmit={handleValidSubmit}
          display="flex"
          flexFlow="column"
          flex="0 1 100%"
        >
          <FormField
            name="markdown"
            label={null}
            helpText={<MarkdownHelpText />}
            renderInput={(props) => (
              <Textarea {...props} minH="5em" h="full" resize="vertical" />
            )}
            display="flex"
            flexFlow="column"
            flex="1 1 100%"
            my={0}
            sx={{ ".chakra-skeleton": { flex: "0 1 100%" } }}
          />
        </Form>
      ) : (
        <Well
          flex="1 1 100%"
          minH="5em"
          overflowY="auto"
          bg="gray.50"
          borderWidth={1}
          borderColor="gray.200"
          rounded="md"
          position="relative"
        >
          {retailer?.entityContext?.markdown ? (
            <>
              <CopyButton
                label="Copy Markdown"
                value={retailer.entityContext.markdown}
                position="absolute"
                top={2}
                right={2}
              />
              <MarkdownText markdown={retailer.entityContext.markdown} />
            </>
          ) : (
            <Text color="gray.500">No context has been added yet.</Text>
          )}
        </Well>
      )}
    </Flex>
  );
}
